import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Globe, Mail } from "lucide-react";

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  x: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

export function Footer() {
  const { t } = useLang();
  const [site, setSite] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    apiGet("/site")
      .then(setSite)
      .catch(() => setSite({ socials: [], pages: [] }));
  }, []);

  const platformLinks = [
    { to: "/modules", label: t("nav.modules") },
    { to: "/secteurs", label: t("nav.sectors") },
    { to: "/tarifs", label: t("nav.pricing") },
    { to: "/demo", label: t("nav.demo") },
  ];
  const companyLinks = [
    { to: "/a-propos", label: t("nav.about") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <footer className="bg-marine text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white p-1.5">
                <img src="/brand/icon.png" alt="MANAGONE" className="h-full w-full object-contain" />
              </div>
              <span className="font-display text-lg font-extrabold tracking-tight text-white">MANAGONE</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("footer.tagline")}</p>
            <p className="mt-3 font-display text-sm font-bold text-gold" data-testid="footer-slogan">
              {t("footer.slogan")}
            </p>
            <a
              href="mailto:contact@managone.com"
              className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-gold"
              data-testid="footer-email"
            >
              <Mail className="h-4 w-4 text-gold" /> contact@managone.com
            </a>
            {site?.socials?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("footer.follow")}</p>
                <div className="mt-3 flex gap-2">
                  {site.socials.map((s) => {
                    const Icon = SOCIAL_ICONS[(s.network || "").toLowerCase()] || Globe;
                    return (
                      <a
                        key={s.id}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={s.name}
                        data-testid={`social-${(s.network || "link").toLowerCase()}`}
                        className="rounded-lg border border-white/15 p-2 text-slate-300 transition-colors hover:border-gold hover:text-gold"
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="font-display text-sm font-bold uppercase tracking-widest text-white">{t("footer.platform")}</p>
            <ul className="mt-4 space-y-2.5">
              {platformLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-gold" data-testid={`footer-platform-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-display text-sm font-bold uppercase tracking-widest text-white">{t("footer.company")}</p>
            <ul className="mt-4 space-y-2.5">
              {companyLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-gold" data-testid={`footer-company-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-display text-sm font-bold uppercase tracking-widest text-white">{t("footer.legal")}</p>
            {site?.pages?.length > 0 ? (
              <ul className="mt-4 space-y-2.5" data-testid="footer-legal-list">
                {site.pages.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/p/${p.slug}`} className="text-sm text-slate-400 transition-colors hover:text-gold" data-testid={`footer-legal-${p.slug}`}>
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-slate-500">—</p>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row">
          <p className="text-xs text-slate-500" data-testid="footer-copyright">
            © {year} MANAGONE. {t("footer.rights")}
          </p>
          <p className="font-display text-xs font-semibold text-gold/80">{t("footer.slogan")}</p>
        </div>
      </div>
    </footer>
  );
}
